import { getAuthHeader } from './auth'

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  body?: unknown
  auth?: boolean
}

// Get tenant slug from current URL path (e.g., /t/demo/admin -> demo)
function getTenantSlugFromPath(): string | null {
  const match = window.location.pathname.match(/^\/t\/([^\/]+)/)
  return match ? match[1] : null
}

// Get API base path - includes tenant prefix if on a tenant route
function getApiBase(): string {
  const slug = getTenantSlugFromPath()
  return slug ? `/t/${slug}` : ''
}

export async function api<T = unknown>(endpoint: string, options: ApiOptions = {}): Promise<T> {
  const { method = 'GET', body, auth = false } = options
  
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(auth ? getAuthHeader() : {}),
  }
  
  // For tenant API calls, prepend the tenant path prefix
  const base = endpoint.startsWith('/api/') ? getApiBase() : ''
  
  const response = await fetch(`${base}${endpoint}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  })
  
  const data = await response.json() as T & { error?: string }
  
  if (!response.ok) {
    throw new Error(data.error || `API error: ${response.status}`)
  }
  
  return data
}

// Platform API (no tenant context needed)
export const platformApi = {
  auth: (email: string, password: string) =>
    api('/platform/auth', { method: 'POST', body: { email, password } }),
  
  getStats: () =>
    api('/platform/stats', { auth: true }),
  
  getTenants: () =>
    api<{ tenants: any[] }>('/platform/tenants', { auth: true }),
  
  createTenant: (data: any) =>
    api('/platform/tenants', { method: 'POST', body: data, auth: true }),
}

// Tenant API (automatically uses tenant context from URL)
export const tenantApi = {
  getConfig: () =>
    api<{ tenant: any }>('/api/config'),
  
  auth: (email: string, password: string) =>
    api('/api/auth', { method: 'POST', body: { email, password } }),
  
  getCamps: (includeArchived = false) =>
    api<{ camps: any[] }>(`/api/camps?includeArchived=${includeArchived}`),
  
  createCamp: (data: any) =>
    api('/api/camps', { method: 'POST', body: data, auth: true }),
  
  getPricing: (campId?: number) =>
    api<{ items: any[] }>(`/api/pricing${campId ? `?campId=${campId}` : ''}`),
  
  createPaymentIntent: (data: any) =>
    api('/api/create-payment-intent', { method: 'POST', body: data }),
  
  submitRegistration: (data: any) =>
    api('/api/submit', { method: 'POST', body: data }),
  
  getRegistrations: (campId?: number) =>
    api<{ registrations: any[] }>(`/api/registrations${campId ? `?campId=${campId}` : ''}`, { auth: true }),
  
  startStripeOnboard: (returnUrl: string, refreshUrl: string) =>
    api<{ url: string }>('/api/stripe/onboard', { method: 'POST', body: { returnUrl, refreshUrl }, auth: true }),
}
